import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { Trash2, RotateCcw, AlertTriangle, Activity, FileText, Shield } from 'lucide-react';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Governance page — Safe-Delete recycle bin + approval queue + audit log.
// Visible at /governance. Org-admin only.
// ─────────────────────────────────────────────────────────────────────────────
export default function Governance() {
  const { permissions } = useAuth();
  const role = permissions?.role;
  const isOrgAdmin = ['owner', 'enterprise', 'super_admin', 'platform_admin', 'platform_owner'].includes(role);

  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!isOrgAdmin) return;
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.governanceTrash(),
        api.governanceAuditLog({ limit: 200 }),
      ]);
      setPending(p.data || []);
      setAudit(a.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load governance');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const restore = async (rid) => {
    try { await api.governanceRestore(rid); toast.success('Restored'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
  };
  const purge = async (rid) => {
    if (!window.confirm('Permanently delete this record? This cannot be undone.')) return;
    try { await api.governancePurge(rid); toast.success('Permanently deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Purge failed'); }
  };

  if (!isOrgAdmin) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto mt-20 p-8 rounded-2xl border border-amber-200 bg-amber-50 text-center">
          <Shield className="h-10 w-10 mx-auto text-amber-600" />
          <h1 className="mt-4 text-xl font-semibold text-amber-900">Org admin access required</h1>
          <p className="mt-2 text-sm text-amber-800">Only property owners can review deletion requests and audit logs.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[1200px] mx-auto px-6 py-8" data-testid="governance-page">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0E1320] text-white text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase">
            <Shield className="h-3 w-3" /> Governance
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[#0E1320]">Safe-delete & audit</h1>
          <p className="mt-1 text-[14px] text-[#4A5263]">Approve or reverse deletion requests · 30-day restore window · immutable audit log.</p>
        </div>

        <div className="flex gap-1 border-b border-[#E6EAF0] mb-6" role="tablist">
          {[
            { id: 'pending', label: 'Deletion requests', icon: AlertTriangle, count: pending.length },
            { id: 'audit',   label: 'Audit log',         icon: Activity,      count: audit.length },
          ].map(({ id, label, icon: Icon, count }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`governance-tab-${id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium transition-colors border-b-2 ${
                tab === id ? 'border-[#00734A] text-[#0E1320]' : 'border-transparent text-[#4A5263] hover:text-[#0E1320]'
              }`}>
              <Icon className="h-3.5 w-3.5" />
              {label}
              <span className="ms-1 px-1.5 py-0.5 rounded-full bg-[#F1F4F7] text-[11px] reva-mono">{count}</span>
            </button>
          ))}
        </div>

        {tab === 'pending' && (
          <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-[#FBFCFD] text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">
                <tr>
                  <th className="text-start px-4 py-3 font-medium">Entity</th>
                  <th className="text-start px-4 py-3 font-medium">Requested by</th>
                  <th className="text-start px-4 py-3 font-medium">Reason</th>
                  <th className="text-start px-4 py-3 font-medium">Deleted</th>
                  <th className="text-start px-4 py-3 font-medium">Restore window</th>
                  <th className="text-end px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map(r => (
                  <tr key={r.id} className="border-t border-[#F1F4F7]" data-testid={`governance-request-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-[#0E1320] font-medium">{r.entity_name}</div>
                      <div className="text-[11px] text-[#71777E] reva-mono">{r.entity_type} · {r.entity_id?.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3 text-[#4A5263]">{r.requested_by_email}<div className="text-[11px] text-[#71777E]">{r.requested_by_role}</div></td>
                    <td className="px-4 py-3 text-[#4A5263]">{r.reason || <span className="text-[#9CA3AF]">—</span>}</td>
                    <td className="px-4 py-3 text-[12px] text-[#4A5263]">{r.soft_deleted_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-3 text-[12px] text-[#4A5263]">{r.restore_until?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-end space-x-2 whitespace-nowrap">
                      <button onClick={() => restore(r.id)} data-testid={`governance-restore-${r.id}`}
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-700 hover:text-emerald-900">
                        <RotateCcw className="h-3.5 w-3.5" /> Restore
                      </button>
                      <button onClick={() => purge(r.id)} data-testid={`governance-purge-${r.id}`}
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-red-700 hover:text-red-900">
                        <Trash2 className="h-3.5 w-3.5" /> Purge
                      </button>
                    </td>
                  </tr>
                ))}
                {pending.length === 0 && !loading && (
                  <tr><td colSpan={6} className="text-center px-4 py-10 text-[#71777E]">Nothing in the recycle bin. Good operational hygiene.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'audit' && (
          <div className="rounded-xl border border-[#E6EAF0] bg-white">
            <ul className="divide-y divide-[#F1F4F7]" data-testid="governance-audit-list">
              {audit.map(e => (
                <li key={e.id} className="px-4 py-3 flex items-start gap-3 text-[12.5px]">
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-medium reva-mono uppercase tracking-[0.12em] ${
                    e.action === 'soft_delete' ? 'bg-amber-100 text-amber-800' :
                    e.action === 'purge' ? 'bg-red-100 text-red-800' :
                    e.action === 'restore' ? 'bg-emerald-100 text-emerald-800' :
                    'bg-[#F1F4F7] text-[#0E1320]'}`}>
                    {e.action}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0E1320]"><span className="font-medium">{e.entity_type}</span> · {e.entity_id?.slice(0, 12) || '—'}</div>
                    <div className="text-[#71777E]">{e.actor_email} · {e.actor_role}</div>
                  </div>
                  <span className="text-[11px] text-[#71777E] reva-mono whitespace-nowrap">{e.created_at?.slice(0, 19).replace('T', ' ')}</span>
                </li>
              ))}
              {audit.length === 0 && <li className="text-center px-4 py-10 text-[#71777E]">No audit events yet.</li>}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}
